import { z } from "zod";
import { createResolver } from "./parameter-resolver";
export const changesetMetadataSchema = z.object({
    v: z.literal(1),
    textEntries: z.array(z.object({
        typeCode: z.string(),
        lineTypeId: z.number().int().nullable(),
        text: z.string().optional(),
        checkbox: z.boolean().optional(),
    })),
    reason: z.string().nullable().optional(),
    sourceProtocolId: z.number().int().nullable().optional(),
    operatorId: z.number().int().nullable().optional(),
    /**
     * The planned item (scheduled action) this changeset fulfilled. Travels in
     * the metadata because a dispatched changeset's row is written by the
     * follower from the armed event, which is all it has.
     */
    scheduledActionId: z.number().int().nullable().optional(),
    bind: z.literal(true).optional(),
    /** Signals an operator overrode while applying a protocol; their rows are the operator's, not the protocol's. */
    overrides: z.array(z.string()).optional(),
    /**
     * Rows these entries temporarily supersede, by signal: the `overrides_id` a
     * signal's first row takes, so that reverting can re-establish what that row
     * governed. Distinct from `overrides` above, which is about provenance during
     * a protocol application. Rides the metadata so the follower reconstructs the
     * pointer from the armed event alone.
     */
    overrideTargets: z
        .array(z.object({ signal: z.string(), rowId: z.number().int() }))
        .optional(),
    /** Readings materialised into constant rows: the attribute, the raw reading and its sample time. */
    captures: z
        .array(z.object({
        signal: z.string(),
        attribute: z.string(),
        value: z.number(),
        atMs: z.number(),
    }))
        .optional(),
    /** Capture entries an armed changeset still holds (no row yet), so a re-push rebuilt from rows keeps them. */
    pendingCaptures: z
        .array(z.object({ signal: z.string(), source: z.string() }))
        .optional(),
});
const EMPTY_METADATA = { v: 1, textEntries: [] };
/**
 * The `ArmChangeset` body as the log stores it (protojson: camelCase keys,
 * oneofs flattened, int64 as a decimal string), read back into the domain
 * shape. The trigger is `now: {}`, `elapsedS` or `atUs`; `atUs` is unix
 * microseconds, the node's wall clock. Metadata is the string BrewBase put
 * there; anything else parses to the empty metadata, which is what a
 * changeset the node's own screen produced carries.
 */
export function parseArmedChangeset(body) {
    const name = typeof body.name === "string" ? body.name : "";
    if (!name)
        return null;
    const rawEntries = Array.isArray(body.entries)
        ? body.entries
        : [];
    const entries = [];
    for (const e of rawEntries) {
        const signal = typeof e.signal === "string" ? e.signal : "";
        if (!signal)
            continue;
        const set = e.set;
        const rule = e.rule;
        const capture = e.capture;
        if (e.clear !== undefined) {
            entries.push({ signal, clear: true });
        }
        else if (rule && typeof rule.expression === "string") {
            entries.push({ signal, rule: { expression: rule.expression } });
        }
        else if (capture && typeof capture.source === "string") {
            entries.push({ signal, capture: { source: capture.source } });
        }
        else if (set && "ramp" in set) {
            const ramp = set.ramp;
            entries.push({
                signal,
                set: {
                    ramp: {
                        to: Number(ramp.to ?? 0),
                        durationS: Number(ramp.durationS ?? 0),
                    },
                },
            });
        }
        else if (set) {
            entries.push({ signal, set: { constant: Number(set.constant ?? 0) } });
        }
    }
    return {
        name,
        trigger: parseArmedTrigger(body),
        entries,
        metadata: parseChangesetMetadata(body.metadata),
    };
}
export function parseArmedTrigger(body) {
    if (body.atUs !== undefined && body.atUs !== null) {
        const atMs = Number(BigInt(String(body.atUs)) / 1000n);
        if (Number.isFinite(atMs))
            return { kind: "at", atMs };
    }
    if (body.elapsedS !== undefined && body.elapsedS !== null) {
        const elapsedS = Number(body.elapsedS);
        if (Number.isFinite(elapsedS))
            return { kind: "elapsed", elapsedS };
    }
    return { kind: "now" };
}
/** The metadata string of an armed changeset, or the empty metadata when it is not ours. */
export function parseChangesetMetadata(raw) {
    if (typeof raw !== "string" || !raw)
        return EMPTY_METADATA;
    try {
        const parsed = changesetMetadataSchema.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : EMPTY_METADATA;
    }
    catch {
        return EMPTY_METADATA;
    }
}
const NAME_MAX = 64;
/** Deterministic upsert key `bb-<fermentation>-<kind>-<epoch ms>`, at most 64 chars. */
export function changesetName(fermentationId, kind, at) {
    const head = `bb-${fermentationId}-`;
    const tail = `-${at.getTime()}`;
    return head + kind.slice(0, NAME_MAX - head.length - tail.length) + tail;
}
/**
 * The valueless numeric entries of a protocol whose type is prompted or
 * measured: what the apply dialog must collect or preview, and what the
 * service refuses to apply without.
 */
export function requiredInputs(entries, types) {
    const out = [];
    for (const e of entries) {
        const type = types.get(e.typeCode);
        if (!type ||
            type.kind !== "NUMBER" ||
            e.formula !== null ||
            e.numberValue !== null)
            continue;
        const source = type.valueSource ?? "authored";
        if (source === "authored")
            continue;
        out.push({
            signal: e.signal,
            source,
            attribute: type.sourceAttribute ?? null,
        });
    }
    return out;
}
const SPECIFIC_RATE_SUFFIX = "_specific_rate_sp";
/**
 * The rule that turns a line's specific feed rate into its absolute rate
 * (decision 0001): the line's `rate_sp` is its `specific_rate_sp` times the
 * target start weight, both in canonical units, so the rule is factor-free.
 */
export function specificRateRule(specificSignal) {
    if (!specificSignal.endsWith(SPECIFIC_RATE_SUFFIX) ||
        specificSignal.length === SPECIFIC_RATE_SUFFIX.length)
        return null;
    const line = specificSignal.slice(0, -SPECIFIC_RATE_SUFFIX.length);
    return {
        signal: `${line}_rate_sp`,
        expression: `${specificSignal} * target_start_weight`,
    };
}
/**
 * Applying a protocol: formulas install rules; numbers ramp when authored with
 * a duration or when the type is rampable and the application has one, else
 * they step; text and checkbox entries ride along in the metadata. A specific
 * feed rate for a line also installs the rule deriving that line's absolute
 * rate, unless the protocol authors the absolute rate itself.
 *
 * A valueless number takes the value the operator `supplied`; failing that, a
 * measured type becomes a capture of its attribute (the service resolves the
 * attribute to a reading or a node signal), and anything else is skipped.
 * A measured value never ramps: a reading is a step.
 *
 * `inForce` is what each signal already resolves to at the trigger, and a
 * value equal to it is written as a constant however long the application's
 * ramp is. An application carries one duration for the whole protocol, but a
 * ramp is a property of a change, not of an application: a protocol that moves
 * the dilution rate and restates a temperature is one ramp and one no-op, and
 * stamping the duration on both makes every restated parameter look like it is
 * ramping for a day. Absent map, absent entry and a null entry all mean "not
 * known", and the duration applies as before.
 *
 * A protocol may state one signal twice at the same instant: a constant
 * lead-in and the ramp that continues from it, passed in ordinal order. They
 * become two wire entries in this order, which is what gives the ramp an
 * origin -- the node applies a changeset's entries in sequence, each reading
 * the setpoint the one before it left, and the resolver reads the preceding
 * row the same way. What is in force therefore advances entry by entry, so the
 * second member is compared against the value its lead-in just set rather than
 * against what the signal held before the application.
 */
export function buildProtocolChangeset(entries, types, durationH, trigger, name, supplied = new Map(), inForce = new Map()) {
    const wire = [];
    const textEntries = [];
    const authored = new Set(entries.map((e) => e.signal));
    // What each signal holds as the entries are emitted, so a lead-in is visible
    // to the ramp that follows it. Seeded with what was in force before.
    const running = new Map(inForce);
    // Order is load-bearing and invisible: a lead-in emitted after its ramp
    // gives the ramp nothing to leave from, so it steps straight to its target
    // and the lead-in then pulls the signal back down. On a feed that is a
    // sustained overdose followed by a drop, from entries that look right in
    // every list that shows them. Anything that reorders a protocol's entries
    // should fail here rather than ship that.
    const lastOrdinal = new Map();
    for (const e of entries) {
        const ordinal = e.entryOrdinal ?? 0;
        const previous = lastOrdinal.get(e.signal);
        if (previous !== undefined && ordinal <= previous) {
            throw new Error(`Protocol entries for '${e.signal}' are out of order: ordinal ${ordinal} follows ${previous}.`);
        }
        lastOrdinal.set(e.signal, ordinal);
    }
    for (const e of entries) {
        const type = types.get(e.typeCode);
        if (e.formula !== null) {
            wire.push({ signal: e.signal, rule: { expression: e.formula } });
        }
        else if (type?.kind === "TEXT" || type?.kind === "CHECKBOX") {
            textEntries.push({
                typeCode: e.typeCode,
                lineTypeId: e.lineTypeId,
                ...(e.text !== null ? { text: e.text } : {}),
                ...(e.checkbox !== null ? { checkbox: e.checkbox } : {}),
            });
        }
        else if (e.numberValue !== null ||
            supplied.has(e.signal) ||
            type?.valueSource === "measured") {
            const value = e.numberValue ?? supplied.get(e.signal) ?? null;
            if (value === null) {
                wire.push({
                    signal: e.signal,
                    capture: { source: type?.sourceAttribute ?? "" },
                });
                continue;
            }
            const unchanged = running.get(e.signal) === value;
            const rampH = type?.valueSource === "measured" || unchanged
                ? null
                : (e.rampDurationH ?? (type?.ramps ? durationH : null));
            wire.push(rampH !== null && rampH > 0
                ? {
                    signal: e.signal,
                    set: { ramp: { to: value, durationS: rampH * 3600 } },
                }
                : { signal: e.signal, set: { constant: value } });
            running.set(e.signal, value);
            const rule = e.typeCode === "specific_rate_sp" ? specificRateRule(e.signal) : null;
            if (rule && !authored.has(rule.signal)) {
                wire.push({
                    signal: rule.signal,
                    rule: { expression: rule.expression },
                });
            }
        }
    }
    return { name, trigger, entries: wire, metadata: { v: 1, textEntries } };
}
/**
 * A protocol application with the operator's overrides on top: an override
 * replaces the protocol's entry for its signal or adds one, after the
 * protocol's, and is named in the metadata so its rows keep the operator's
 * provenance.
 */
export function withOverrides(changeset, overrides) {
    if (overrides.length === 0)
        return changeset;
    const signals = new Set(overrides.map((e) => e.signal));
    return {
        ...changeset,
        entries: [
            ...changeset.entries.filter((e) => !signals.has(e.signal)),
            ...overrides,
        ],
        metadata: { ...changeset.metadata, overrides: [...signals] },
    };
}
/**
 * Binding push: what the node must hold so that it agrees with the rows at
 * `nowMs`. A ruled code sends its rule; a pinned code sends the rule and then
 * the pin; a ramp in flight sends its current value and then the remainder.
 *
 * A code cleared after it held something sends the clear, because the node this
 * is pushed to may still be holding that value and nothing else here would take
 * it away. A code that never held anything sends nothing: a slot nobody ever
 * filled in reads the same as a clear, but there is nothing on the node to undo.
 */
export function entriesFromRows(rows, nowMs) {
    const resolver = createResolver(rows);
    const out = [];
    for (const code of resolver.codes()) {
        const history = rows
            .filter((r) => r.code === code && r.effectiveAtMs <= nowMs)
            .sort((a, b) => a.effectiveAtMs - b.effectiveAtMs || a.id - b.id);
        const latest = history.at(-1);
        const value = resolver.valueAt(code, nowMs);
        if (!latest)
            continue;
        if (value === null) {
            const cleared = latest.formula === null &&
                latest.numberValue === null &&
                history.some((r) => r !== latest && (r.numberValue !== null || r.formula !== null));
            if (cleared)
                out.push({ signal: code, clear: true });
            continue;
        }
        if (latest.formula !== null) {
            out.push({ signal: code, rule: { expression: latest.formula } });
            continue;
        }
        const rule = history.findLast((r) => r.formula !== null);
        if (rule?.formula)
            out.push({ signal: code, rule: { expression: rule.formula } });
        out.push({ signal: code, set: { constant: value } });
        const remainingH = latest.rampDurationH !== null
            ? latest.rampDurationH - (nowMs - latest.effectiveAtMs) / 3_600_000
            : 0;
        if (remainingH > 0 && latest.numberValue !== null) {
            out.push({
                signal: code,
                set: { ramp: { to: latest.numberValue, durationS: remainingH * 3600 } },
            });
        }
    }
    return out;
}
//# sourceMappingURL=changeset.js.map